// The parent app's school calendar (PMU-265): a card on Home with what's next and today's lessons,
// and a page with each child's class timetable (one day at a time, for a phone) and what's coming.
import { useEffect, useState } from "react";
import { Icon, Spinner } from "@vavr/ui";
import { api } from "@vavr/screens/shared";
import { clock, EventList, KIND, longDay, when, WEEKDAYS, type CalEvent, type Period, type Slot } from "./screens.tsx";

interface School { site: { id: string; name: string; tz: string }; today: string; events: CalEvent[]; schedule: { name: string; periods: Period[] } | null; classes: { child: string; room: string; slots: Slot[] }[] }
let cache: School[] | null = null;
function useParentCalendar() {
  const [d, setD] = useState<School[] | null>(cache);
  useEffect(() => { void api<{ schools: School[] }>("GET", "/api/v1/guardian/calendar").then((r) => { cache = r.schools; setD(r.schools); }, () => setD((x) => x ?? [])); }, []);
  return d;
}
const weekdayOf = (d: string) => new Date(`${d}T12:00:00Z`).getUTCDay();

const nowHHMM = (tz: string) => new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date());
const dayParts = (d: string) => ({ n: Number(d.slice(8)), w: new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }) });

/** Home: a small calendar: today (closed, a moved day, or each child's lesson now and next) and the next three items. */
export function ParentCalendarCard({ open }: { open: () => void }) {
  const d = useParentCalendar();
  if (d && !d.length) return null;
  const s = d?.[0];
  const today = s?.today ?? "";
  const events = (d ?? []).flatMap((x) => x.events.filter((e) => e.end_date >= x.today)).sort((a, b) => a.start_date.localeCompare(b.start_date) || (a.start_time ?? "").localeCompare(b.start_time ?? ""));
  const note = s?.events.find((e) => e.start_date <= today && e.end_date >= today && ["no_school", "holiday", "early_release", "late_start"].includes(e.kind));
  const closed = note && (note.kind === "no_school" || note.kind === "holiday");
  const coming = events.filter((e) => e !== note).slice(0, 3);
  // Each child's lesson now and the next one today, on the usual bell times.
  const lessons = s && s.schedule && !closed ? s.classes.map((c) => {
    const wd = weekdayOf(today), now = nowHHMM(s.site.tz);
    const day = s.schedule!.periods.map((p) => ({ p, sl: c.slots.find((x) => x.weekday === wd && x.period_code === p.code) })).filter((x) => x.sl);
    const cur = day.find((x) => x.p.start_time <= now && x.p.end_time > now), nxt = day.find((x) => x.p.start_time > now);
    if (!cur && !nxt) return null;
    return `${c.child} · ${cur ? `${cur.sl!.subject} now` : ""}${cur && nxt ? " · " : ""}${nxt ? `${nxt.sl!.subject} at ${clock(nxt.p.start_time)}` : ""}`;
  }).filter((x): x is string => !!x) : [];
  return (
    <button type="button" className="pcal-tile" onClick={open} aria-label="School calendar: see all">
      <span className="pcal-tile__head">
        <span className="pcal-tile__icon" aria-hidden="true"><Icon name="calendar" /></span>
        <b>School calendar</b>
        <span className="pcal-tile__all">See all <Icon name="chevron-right" /></span>
      </span>
      {!d ? <span className="pcal-tile__empty">Days off, events and your child's lessons</span> : <>
        {(note || lessons.length > 0) && (
          <span className={`pcal-tile__today${closed ? " pcal-tile__today--closed" : note ? " pcal-tile__today--moved" : ""}`}>
            <span className="pcal-tile__label">Today</span>
            {note ? <span><b>{note.title}</b>{note.minutes ? ` · ${note.minutes} min ${note.kind === "early_release" ? "early" : "late"}` : ""}</span>
              : lessons.map((l) => <span key={l}>{l}</span>)}
          </span>
        )}
        {coming.length ? (
          <span className="pcal-tile__list">
            {coming.map((e) => {
              const p = dayParts(e.start_date);
              return (
                <span key={e.id} className="pcal-tile__item">
                  <span className={`pcal-tile__date pcal-tag--${KIND[e.kind].tone}`}><b>{p.n}</b><span>{p.w}</span></span>
                  <span className="pcal-tile__what"><b>{e.title}</b><span>{when(e)}{e.location ? ` · ${e.location}` : ""}</span></span>
                </span>
              );
            })}
          </span>
        ) : <span className="pcal-tile__empty">Nothing coming up on the calendar.</span>}
      </>}
    </button>
  );
}

export function ParentCalendarPage() {
  const d = useParentCalendar();
  if (!d) return <div className="pmu-center"><Spinner label="Loading" /></div>;
  if (!d.length) return <p className="pmu-muted">Your school has not shared a calendar yet.</p>;
  return (
    <div className="pmu-stack pcal-parent">
      {d.map((s) => (
        <section key={s.site.id} className="pmu-stack" aria-label={s.site.name}>
          {d.length > 1 && <h2 className="pcal-parent__school">{s.site.name}</h2>}
          {s.classes.filter((c) => c.slots.length && s.schedule).map((c) => <DayTimetable key={c.child} child={c.child} room={c.room} slots={c.slots} periods={s.schedule!.periods} today={s.today} />)}
          <h3 className="pcal-parent__h">Coming up</h3>
          <EventList events={s.events.filter((e) => e.end_date >= s.today)} empty="Nothing on the calendar yet." level={4} />
        </section>
      ))}
    </div>
  );
}

/** One child's lessons for a chosen weekday (today, or Monday at the weekend). */
function DayTimetable({ child, room, slots, periods, today }: { child: string; room: string; slots: Slot[]; periods: Period[]; today: string }) {
  const t = weekdayOf(today);
  const [day, setDay] = useState(t === 0 || t === 6 ? 1 : t);
  const list = periods.map((p) => ({ p, s: slots.find((x) => x.weekday === day && x.period_code === p.code) })).filter((x) => x.s || x.p.kind !== "class");
  return (
    <article className="pcal-day-tt">
      <header><b>{child}'s day</b><span className="pmu-small pmu-muted">{room}</span></header>
      <div className="pcal-switch" role="tablist" aria-label="Day">
        {WEEKDAYS.filter((w) => w.d >= 1 && w.d <= 5).map((w) => <button type="button" role="tab" key={w.d} aria-selected={w.d === day} onClick={() => setDay(w.d)}>{w.l}{w.d === t ? " ·" : ""}</button>)}
      </div>
      {!list.some((x) => x.s) ? <p className="pmu-small pmu-muted">No lessons set for {WEEKDAYS.find((w) => w.d === day)?.long}.</p> : (
        <ol className="pcal-day-tt__list">
          {list.map(({ p, s }) => (
            <li key={p.code} className={!s ? "pcal-day-tt__break" : undefined}>
              <span className="pcal-day-tt__time">{clock(p.start_time)}</span>
              <span><b>{s?.subject ?? p.name}</b>{s && (s.teacher || s.location) && <span className="pmu-small pmu-muted"> {[s.teacher, s.location].filter(Boolean).join(" · ")}</span>}</span>
            </li>
          ))}
        </ol>
      )}
      {day === t && <p className="pmu-small pmu-muted">Today, {longDay(today)}</p>}
    </article>
  );
}
